// ===========================================
// COLUMNS: Tabla de Vencimientos
// ===========================================
// Columnas para TanStack Table

import { createColumnHelper } from '@tanstack/react-table'
import type { VencimientoConCliente } from '@/types/vencimientos'
import type { CatalogoImpuesto } from '@/types/impuestos'
import { getImpuestoColorClasses } from '@/lib/impuestoColorResolver'
import { Badge } from '@/components/ui/badge'
import { CopyableCell } from '@/components/ui/copyable-cell'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CheckCircle, Eye, ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, DollarSign, Receipt, FileSpreadsheet, EyeOff, Undo2, Bell, Mail } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { ESTADOS_VENCIMIENTO_FISCAL } from '@/types/vencimientos'
import { formatPeriodo, periodoSortKey } from '@/lib/formatPeriodo'

const columnHelper = createColumnHelper<VencimientoConCliente>()

const simplificarTipoObligacion = (tipo: string | null | undefined): string | null => {
  if (!tipo) return null
  const lower = tipo.toLowerCase()
  if (lower.includes('anticipo')) return 'Anticipo'
  if (lower.includes('declaración jurada') || lower.includes('declaracion jurada') || lower.includes('ddjj')) return 'DDJJ'
  if (lower.includes('presentación') || lower.includes('presentacion')) return 'Presentación'
  if (lower.includes('ingreso de saldo') || lower.includes('saldo resultante')) return 'Saldo'
  if (lower.includes('ingreso del impuesto') || lower.includes('ingreso de cuota') || lower.includes('pago')) return 'Pago'
  if (lower.includes('retenciones') || lower.includes('percepciones')) return 'Ret/Perc'
  if (lower.includes('transferencia electrónica') || lower.includes('transferencia electronica')) return 'Info'
  if (lower.includes('memoria') || lower.includes('estados contables')) return 'Balances'
  if (lower.includes('recategorizaci')) return 'Recategorización'
  if (lower.includes('libro de iva') || lower.includes('registraci')) return 'Libro IVA'
  if (lower.includes('estudio de precios')) return 'Precios Transf.'
  return null
}

export const getEstadoEfectivo = (v: VencimientoConCliente): string => {
  const extended = v as unknown as { estado_efectivo?: string }
  return extended.estado_efectivo ?? v.estado ?? ''
}

const getEstadoBadge = (estado: string) => {
  const config = ESTADOS_VENCIMIENTO_FISCAL[estado as keyof typeof ESTADOS_VENCIMIENTO_FISCAL]
  if (!config) return <span className="text-xs text-muted-foreground">&mdash;</span>

  return (
    <Badge variant="outline" className={`${config.color} ${config.bgColor} border-0`}>
      {config.label}
    </Badge>
  )
}

/**
 * ORR-232: clave estable de un vencimiento para cruzarlo con sus alertas.
 *
 * El `id` de las filas virtuales se regenera en cada lectura de la vista, así que no sirve:
 * las del calendario AFIP se identifican por `calendario_afip_id` y las físicas
 * (recurrentes) por su propio id. La página arma el Set con esta misma función para que
 * las dos puntas no puedan divergir.
 */
export const claveAlerta = (v: Pick<VencimientoConCliente, 'cliente_id' | 'id'> & { calendario_afip_id?: string | null }) =>
  `${v.cliente_id}:${v.calendario_afip_id || v.id}`

interface ColumnCallbacks {
  onMarcarPresentado: (vencimiento: VencimientoConCliente) => void
  onProgramarAlerta?: (vencimiento: VencimientoConCliente) => void
  /** Claves (ver claveAlerta) de los vencimientos que ya tienen alerta del usuario actual */
  alertasAncla?: Set<string>
  onVerDetalles?: (vencimiento: VencimientoConCliente) => void
  onRegistrarPago?: (vencimiento: VencimientoConCliente) => void
  onRegistrarVEP?: (vencimiento: VencimientoConCliente) => void
  onPlanPago?: (vencimiento: VencimientoConCliente) => void
  onDescartar?: (vencimiento: VencimientoConCliente) => void
  onRestaurar?: (vencimiento: VencimientoConCliente) => void
  onRevertirPresentado?: (vencimiento: VencimientoConCliente) => void
  /** Opens the client notification dialog of a presented vencimiento. */
  onReenviarNotificacion?: (vencimiento: VencimientoConCliente) => void
  /** ORR-230 fase 5: abre la ficha del cliente desde su nombre en la planilla */
  onAbrirCliente?: (vencimiento: VencimientoConCliente) => void
  catalogo?: CatalogoImpuesto[]
}

export const createVencimientosColumns = (
  onMarcarPresentado: (vencimiento: VencimientoConCliente) => void,
  onVerDetalles?: (vencimiento: VencimientoConCliente) => void,
  catalogo?: CatalogoImpuesto[],
  callbacks?: Partial<ColumnCallbacks>
) => {
  const onDescartar = callbacks?.onDescartar
  const onRestaurar = callbacks?.onRestaurar
  const onRevertirPresentado = callbacks?.onRevertirPresentado
  const onReenviarNotificacion = callbacks?.onReenviarNotificacion
  const onProgramarAlerta = callbacks?.onProgramarAlerta
  const onAbrirCliente = callbacks?.onAbrirCliente
  const alertasAncla = callbacks?.alertasAncla
  return [
  // Orden: Cliente → CUIT → Impuesto → Período → Vencimiento → Estado → Acciones
  columnHelper.accessor('cliente_nombre', {
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 px-2 hover:bg-muted"
          onClick={() => column.toggleSorting()}
        >
          Cliente
          {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
          {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
          {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
        </Button>
      )
    },
    cell: ({ getValue, row }) => (
      <span className="font-medium truncate flex items-center gap-1.5">
        {alertasAncla?.has(claveAlerta(row.original)) && (
          <Bell className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-label="Tiene alerta programada" />
        )}
        {/* Boton y no span con onClick: es un control real, navegable por teclado. La campana
            queda afuera porque es un indicador, no parte de lo que se clickea. */}
        {onAbrirCliente && row.original.cliente_id ? (
          <button
            type="button"
            className="truncate text-left hover:underline hover:text-primary"
            title="Ver la ficha del cliente"
            onClick={() => onAbrirCliente(row.original)}
          >
            {getValue()}
          </button>
        ) : (
          <span className="truncate">{getValue()}</span>
        )}
      </span>
    ),
    size: 200,
  }),

  columnHelper.accessor('cliente_cuit', {
    header: 'CUIT',
    cell: ({ getValue }) => {
      const cuit = getValue()
      if (!cuit) return <span className="text-muted-foreground">-</span>
      const str = cuit.toString().padStart(11, '0')
      const formatted = `${str.slice(0, 2)}-${str.slice(2, 10)}-${str.slice(10)}`
      return (
        <CopyableCell value={str} displayValue={formatted} />
      )
    },
    size: 140,
  }),

  columnHelper.accessor((row) => row.tipo_impuesto || '', {
    id: 'tipo_impuesto',
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 px-2 hover:bg-muted"
          onClick={() => column.toggleSorting()}
        >
          Impuesto
          {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
          {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
          {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
        </Button>
      )
    },
    cell: ({ row, getValue }) => {
      const value = getValue() as string
      if (!value) return null
      const extended = row.original as unknown as { tipo_obligacion?: string | null }
      const tipoCorto = simplificarTipoObligacion(extended.tipo_obligacion)
      return (
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={getImpuestoColorClasses(value, catalogo)}>
            {value}
          </Badge>
          {tipoCorto && (
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{tipoCorto}</span>
          )}
        </div>
      )
    },
    size: 180,
  }),

  columnHelper.accessor((row) => row.periodo, {
    id: 'periodo',
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 px-2 hover:bg-muted"
          onClick={() => column.toggleSorting()}
        >
          Período
          {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
          {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
          {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
        </Button>
      )
    },
    cell: ({ getValue }) => {
      const raw = getValue() as string | null | undefined
      return (
        <span className="text-sm text-muted-foreground" title={raw ?? undefined}>
          {formatPeriodo(raw)}
        </span>
      )
    },
    sortingFn: (a, b) =>
      periodoSortKey(a.original.periodo).localeCompare(periodoSortKey(b.original.periodo)),
    size: 100,
  }),

  columnHelper.accessor('fecha_vencimiento', {
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 px-2 hover:bg-muted"
          onClick={() => column.toggleSorting()}
        >
          Vencimiento
          {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
          {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
          {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
        </Button>
      )
    },
    cell: ({ getValue }) => {
      const fecha = parseISO(getValue() ?? '')
      return (
        <span className="text-sm">
          {format(fecha, "d 'de' MMM", { locale: es })}
        </span>
      )
    },
    size: 120,
  }),

  columnHelper.accessor(
    (row) => getEstadoEfectivo(row),
    {
      id: 'estado',
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 hover:bg-muted"
            onClick={() => column.toggleSorting()}
          >
            Estado
            {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
            {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
            {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
          </Button>
        )
      },
      cell: ({ getValue }) => getEstadoBadge(getValue()),
      size: 110,
    }
  ),

  columnHelper.display({
    id: 'acciones',
    header: () => <span className="sr-only">Acciones</span>,
    cell: ({ row }) => {
      const vencimiento = row.original
      const estadoEfectivo = getEstadoEfectivo(vencimiento)
      const extended = vencimiento as unknown as { monto_determinado?: number | null; pagado?: boolean | null; vep_numero?: string | null; plan_pago_cuotas?: number | null }
      const tieneMonto = extended.monto_determinado != null && extended.monto_determinado > 0
      // Revertir presentación: solo si no avanzó en el ciclo de pago (guarda espejo de la RPC).
      const puedeRevertir = !!onRevertirPresentado && estadoEfectivo === 'presentado' && !extended.pagado && !extended.vep_numero && extended.plan_pago_cuotas == null
      // The notification is offered for every vencimiento that was presented, paid ones included.
      // It needs the presentation date, which the email shows.
      const puedeReenviar =
        !!onReenviarNotificacion &&
        (estadoEfectivo === 'presentado' || !!vencimiento.presentado) &&
        !!vencimiento.fecha_presentacion
      const itemReenviar = puedeReenviar && (
        <DropdownMenuItem onClick={() => onReenviarNotificacion!(vencimiento)}>
          <Mail className="h-4 w-4 mr-2" />
          Enviar notificación
        </DropdownMenuItem>
      )

      // ORR-178: papelera. Descartados muestran quién/cuándo (ORR-198 A4) + "Restaurar" (solo admin).
      if (estadoEfectivo === 'descartado') {
        const audit = vencimiento as unknown as { descartado_por_nombre?: string | null; created_at?: string | null }
        return (
          <div className="flex items-center justify-end gap-2">
            {(audit.descartado_por_nombre || audit.created_at) && (
              <span className="text-[11px] leading-tight text-muted-foreground text-right">
                {audit.descartado_por_nombre && <span className="block">por {audit.descartado_por_nombre}</span>}
                {audit.created_at && <span className="block">{format(parseISO(audit.created_at), 'dd/MM/yy HH:mm', { locale: es })}</span>}
              </span>
            )}
            {onRestaurar && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                onClick={() => onRestaurar(vencimiento)}
              >
                <Undo2 className="h-4 w-4 mr-1" />
                Restaurar
              </Button>
            )}
          </div>
        )
      }

      // Pendiente or vencido: show "Presentar" + dropdown with eliminar
      if (estadoEfectivo === 'pendiente' || estadoEfectivo === 'vencido') {
        return (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-green-600 hover:text-green-700 hover:bg-green-50"
              onClick={() => onMarcarPresentado(vencimiento)}
            >
              <CheckCircle className="h-4 w-4" />
            </Button>
            {(onDescartar || onProgramarAlerta) && (vencimiento.calendario_afip_id || vencimiento.id) && vencimiento.cliente_id && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* ORR-184: "Descartar" (ocultar reversible vía papelera) ahora también
                      en vf materializados pendientes/vencidos, no solo en los virtuales.
                      ORR-198 A4: "Eliminar" (hard delete) se retiró; ahora todo va por Descartar.
                      ORR-234: también sin calendario_afip_id (recurrentes y manuales). Esas filas
                      quedaron sin ninguna forma de sacarse de la lista cuando A4 retiró Eliminar. */}
                  {onProgramarAlerta && (
                    <DropdownMenuItem onClick={() => onProgramarAlerta(vencimiento)}>
                      <Bell className="h-4 w-4 mr-2" />
                      {alertasAncla?.has(claveAlerta(vencimiento)) ? 'Editar alerta' : 'Programar alerta'}
                    </DropdownMenuItem>
                  )}
                  {onDescartar && (
                    <DropdownMenuItem onClick={() => onDescartar(vencimiento)}>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Descartar
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )
      }

      // Presentado with monto > 0: show dropdown with payment actions + eliminar
      if (estadoEfectivo === 'presentado' && tieneMonto && (callbacks?.onRegistrarPago || callbacks?.onRegistrarVEP || callbacks?.onPlanPago)) {
        return (
          <div className="flex justify-end gap-1">
            {onVerDetalles && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-green-600 hover:text-green-700"
                onClick={() => onVerDetalles(vencimiento)}
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {itemReenviar}
                {callbacks?.onRegistrarPago && (
                  <DropdownMenuItem onClick={() => callbacks.onRegistrarPago!(vencimiento)}>
                    <DollarSign className="h-4 w-4 mr-2" />
                    Registrar Pago
                  </DropdownMenuItem>
                )}
                {callbacks?.onRegistrarVEP && (
                  <DropdownMenuItem onClick={() => callbacks.onRegistrarVEP!(vencimiento)}>
                    <Receipt className="h-4 w-4 mr-2" />
                    Registrar VEP
                  </DropdownMenuItem>
                )}
                {callbacks?.onPlanPago && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => callbacks.onPlanPago!(vencimiento)}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Plan de Pago
                    </DropdownMenuItem>
                  </>
                )}
                {puedeRevertir && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onRevertirPresentado!(vencimiento)}>
                      <Undo2 className="h-4 w-4 mr-2" />
                      Revertir presentación
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      }

      // Presentado sin monto/pago/plan: show details + revertir presentación
      if (onVerDetalles || puedeRevertir || puedeReenviar) {
        return (
          <div className="flex justify-end gap-1">
            {onVerDetalles && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-green-600 hover:bg-green-50"
                onClick={() => onVerDetalles(vencimiento)}
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}
            {(puedeRevertir || puedeReenviar) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {itemReenviar}
                  {puedeRevertir && (
                    <>
                      {puedeReenviar && <DropdownMenuSeparator />}
                      <DropdownMenuItem onClick={() => onRevertirPresentado!(vencimiento)}>
                        <Undo2 className="h-4 w-4 mr-2" />
                        Revertir presentación
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )
      }

      return null
    },
    size: 90,
  }),
  ]
}
